/**
 * withProgrammaticScroll — bracket helper for the `chatProgrammaticScroll`
 * flag on the chat host (JAISIU-3496 / G3).
 *
 * ## When to use it
 *
 * Wrap any code path that programmatically writes `scrollTop` on the
 * chat-thread scroll container. The scroll handler in `app-scroll.ts`
 * classifies every `scroll` event as either "user gesture" (feeds the
 * ↑/↓ chevron accumulators + the upward pager + the persisted anchor)
 * or "programmatic" (skipped, baseline only) — and it makes that
 * classification by reading `host.chatProgrammaticScroll`. If the flag
 * is not set, the post-write `scroll` event is treated as a real user
 * gesture, which is exactly what we do NOT want for the two cases that
 * need a viewport correction:
 *
 *   - `loadOlderChatHistory` (JAISIU-3473) shifts `scrollTop` by the
 *     height of the freshly-prepended page so the operator's anchor
 *     row stays visually in place. Without the bracket, the resulting
 *     `scroll` event either arms the ↑ chevron (because the delta
 *     looks like a giant upward scroll of N pixels) or the ↓ chevron
 *     (if the post-prepend scrollTop lands below the pre-fetch
 *     scrollTop due to round-off in the anchor math).
 *
 *   - `restorePersistedScrollAnchor` (JAISIU-3474) places the viewport
 *     on the operator's last-read message id after a session switch /
 *     page reload. Same hazard: a giant single-frame delta that
 *     reaches the scroll handler un-bracketed is mis-classified as a
 *     user gesture and arms the wrong chevron / mis-fires the pager.
 *
 * Before this helper existed both call sites had to remember the
 * try/finally pattern by hand. Forgetting the finally (or the throw
 * branch) leaks the flag to `true` for the rest of the session and
 * silently kills every legitimate scroll-gesture classification.
 * This helper makes the bracket impossible to forget.
 *
 * ## What it guarantees
 *
 *   - The flag is set to `true` BEFORE `fn()` runs.
 *   - The flag is set back to `false` in a `finally` block, so the
 *     flag is cleared even if `fn` throws synchronously or rejects.
 *   - The return value of `fn` (sync or async) is propagated to the
 *     caller unchanged.
 *
 * ## What it does NOT guarantee
 *
 *   - It does NOT prevent nested scroll events from *other* sources.
 *     The helper only marks the scroll event(s) produced by the
 *     `container.scrollTop = ...` write inside `fn`. A wheel event
 *     or a `scrollTo()` triggered by unrelated callers concurrently
 *     with `fn` will still be classified by the scroll handler using
 *     whatever the flag is at the time that event fires — if those
 *     callers want their events bracketed too, they must wrap their
 *     own work in `withProgrammaticScroll` (nested calls are safe,
 *     see below).
 *   - It does NOT scope the bracket to a specific container or
 *     `scrollTop` write. The helper owns the flag for the entire
 *     duration of `fn` — keep `fn` focused on the viewport write and
 *     the awaits around it; do not put unrelated long-running work
 *     inside the bracket.
 *
 * ## Nesting
 *
 *   The helper is safe under interleaved calls. If two callers enter
 *   `withProgrammaticScroll` concurrently (the second starts before
 *   the first's `fn` has resolved), the flag stays `true` until the
 *   innermost `fn` resolves and the outermost one is the last to
 *   clear. The outermost caller's `finally` always restores `false`.
 *   Callers therefore do NOT need to coordinate ordering — they only
 *   need to wrap their own write.
 */
export async function withProgrammaticScroll<T>(
  host: { chatProgrammaticScroll: boolean },
  fn: () => T | Promise<T>,
): Promise<T> {
  host.chatProgrammaticScroll = true;
  try {
    return await fn();
  } finally {
    host.chatProgrammaticScroll = false;
  }
}
